import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, SyntheticEvent } from "react";

interface HoldOptions<T> {
  /** How long a press must last before it counts as a hold. */
  delay?: number;
  /** Pointer drift (px) that cancels a pending hold, so a scroll is never a hold. */
  moveTolerance?: number;
  /**
   * Drag-to-switch, off unless supplied. `selector` marks the rows a finger may
   * land on once the hold has armed, `value` turns the one under the pointer
   * into a payload. It is a parameter rather than a hard-coded attribute so the
   * hook stays generic over T — only the caller knows how to read its own rows.
   */
  dragSwitch?: { selector: string; value: (element: HTMLElement) => T | null };
}

/**
 * Press-and-hold that does not eat ordinary taps.
 *
 * Three rules make that true:
 *  1. 450ms threshold — a deliberate press, not a slightly slow tap.
 *  2. The hold is cancelled as soon as the pointer drifts BEFORE it arms, so
 *     dragging or scrolling over the element never arms it.
 *  3. `didHold` is cleared SYNCHRONOUSLY inside the click handler that follows.
 *     Clearing it on a `setTimeout(..., 0)` races React's click dispatch, and
 *     losing that race is exactly what swallows a tap.
 *
 * On top of that there are two ways in and one way to travel:
 *  - HOLD gives a transient `held` value that dies with the press.
 *  - TAP leaves `pinned` set, so the panel survives the finger lifting; the
 *    caller decides what dismisses it. `active` is what to render.
 *  - Once a hold has ARMED, sliding the finger onto another row switches the
 *    panel to it instead of closing — see the pointer capture in onPointerDown.
 *
 * All of that state — timer, origin, armed, didHold — is ONE set shared by every
 * row, so exactly one gesture may own it at a time. The first pointerdown latches
 * its pointer id and every event from any other pointer is dropped until it ends;
 * without that, a second finger landing on a second row restarts the timer and
 * clears `armed` under the first press, and the first row's panel never closes.
 *
 * The held payload is generic so a list can hold the id of the pressed row.
 */
export function useHoldReveal<T>({
  delay = 450,
  moveTolerance = 10,
  dragSwitch
}: HoldOptions<T> = {}) {
  const [held, setHeld] = useState<T | null>(null);
  const [pinned, setPinned] = useState<T | null>(null);
  const timer = useRef<number | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  // The row whose click still has to be eaten, not a bare flag: it outlives the
  // pointerup, and a second finger tapping a DIFFERENT row in that gap would
  // otherwise spend the flag on its own click and let the held row's click
  // through as a tap — pinning the very row the user was only holding.
  const didHold = useRef<T | null>(null);
  // True for the rest of the gesture once the hold fires. Distinct from
  // `didHold`, which outlives the pointerup so the click it spawns can be
  // swallowed; this one has to be false again the moment the finger is up.
  const armed = useRef(false);
  // The one pointer allowed to drive the shared state above, latched at
  // pointerdown. Null means no gesture is in flight and the next press wins.
  const activePointer = useRef<number | null>(null);
  // The element that captured `activePointer`, kept so the capture can be given
  // back from anywhere — including the window-level backstop below, which has no
  // event target of its own to release from.
  const captor = useRef<Element | null>(null);
  // Read the switch through a ref: callers pass an object literal, which would
  // otherwise be a fresh identity every render and churn the callbacks below.
  const drag = useRef(dragSwitch);

  useEffect(() => {
    drag.current = dragSwitch;
  });

  const clear = useCallback(() => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  useEffect(() => () => clear(), [clear]);

  // Hand the capture back explicitly. Browsers do release it implicitly on
  // pointerup and pointercancel, but not when the captor is torn out of the DOM
  // mid-press — a rival leaving the room takes their pill with it — and a
  // capture nobody owns keeps routing later presses at a node that is gone.
  const releaseCapture = useCallback(() => {
    const element = captor.current;
    const pointerId = activePointer.current;
    captor.current = null;
    if (!element || pointerId === null) return;
    try {
      if (element.hasPointerCapture(pointerId)) element.releasePointerCapture(pointerId);
    } catch {
      // Same as taking the capture: an id the browser has already forgotten
      // throws, and there is nothing left to release.
    }
  }, []);

  const start = useCallback(
    (value: T) => {
      clear();
      didHold.current = null;
      armed.current = false;
      timer.current = window.setTimeout(() => {
        timer.current = null;
        didHold.current = value;
        armed.current = true;
        setHeld(value);
        // A hold always shows whatever the finger is on, so it takes the panel
        // back from a pinned row. Without this `pinned ?? held` would keep
        // rendering a row nobody is touching for the whole press.
        setPinned(null);
        navigator.vibrate?.(12);
      }, delay);
    },
    [clear, delay]
  );

  const end = useCallback(() => {
    clear();
    releaseCapture();
    origin.current = null;
    armed.current = false;
    activePointer.current = null;
    setHeld(null);
  }, [clear, releaseCapture]);

  // The backstop for a press whose element never reports back: unmount the
  // captor mid-gesture and its own pointerup is delivered to a detached node
  // React no longer listens to, so the latch would stay set and every later
  // press on every row would be dropped as a stray second finger. Matching on
  // the id keeps a genuine second finger's release from ending the first press.
  useEffect(() => {
    const finish = (event: PointerEvent) => {
      if (activePointer.current === event.pointerId) end();
    };
    window.addEventListener("pointerup", finish);
    window.addEventListener("pointercancel", finish);
    return () => {
      window.removeEventListener("pointerup", finish);
      window.removeEventListener("pointercancel", finish);
    };
  }, [end]);

  const pin = useCallback((value: T) => setPinned(value), []);
  const unpin = useCallback(() => setPinned(null), []);
  /** Tap the row that is already pinned to dismiss it, any other row to move. */
  const toggle = useCallback(
    (value: T) => setPinned((current) => (current === value ? null : value)),
    []
  );

  /** Spread onto the pressable element. `value` is what `held` becomes. */
  const handlers = (value: T) => ({
    onPointerDown(event: ReactPointerEvent) {
      if (event.button > 0) return;
      // A gesture already owns the shared state; this is a second finger and it
      // gets nothing — no timer, no capture, no events for the rest of its life.
      if (activePointer.current !== null) return;
      activePointer.current = event.pointerId;
      origin.current = { x: event.clientX, y: event.clientY };
      // Capture so the rest of the gesture keeps arriving at THIS element even
      // after the finger has slid off it. Drag-to-switch needs the moves, and
      // more importantly the closing pointerup: uncaptured, it fires on
      // whatever row is underneath and this element never learns the press
      // ended, leaving the panel stuck open.
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
        captor.current = event.currentTarget;
      } catch {
        // Some browsers throw on a pointer id that is already gone. The hold
        // still works off the uncaptured events, so there is nothing to do.
      }
      start(value);
    },
    onPointerMove(event: ReactPointerEvent) {
      if (activePointer.current !== event.pointerId) return;
      if (armed.current) {
        const switcher = drag.current;
        if (!switcher) return;
        // Pointer capture reroutes events but not hit testing, so the document
        // still knows what is actually under the finger. Any panel this hook
        // opened has to stay pointer-events: none or it answers here instead of
        // the row behind it.
        const under = document.elementFromPoint(event.clientX, event.clientY);
        const row = under?.closest<HTMLElement>(switcher.selector) ?? null;
        // Between two rows is a slip, not a dismissal: hold the last one.
        if (!row) return;
        const next = switcher.value(row);
        if (next !== null) {
          // The click a lift spawns is aimed at whatever row the finger ended
          // over, not the one the press began on, so the claim on that click has
          // to travel with the drag — otherwise the last row of a sweep is
          // "tapped" the instant the finger comes up.
          didHold.current = next;
          setHeld((current) => (current === next ? current : next));
        }
        return;
      }
      if (timer.current === null || !origin.current) return;
      const drift = Math.hypot(event.clientX - origin.current.x, event.clientY - origin.current.y);
      if (drift > moveTolerance) clear();
    },
    onPointerUp(event: ReactPointerEvent) {
      if (activePointer.current !== event.pointerId) return;
      end();
    },
    onPointerCancel(event: ReactPointerEvent) {
      if (activePointer.current !== event.pointerId) return;
      end();
    },
    onPointerLeave(event: ReactPointerEvent) {
      if (activePointer.current !== event.pointerId) return;
      // Before the hold arms, leaving is a cancel. After it arms, leaving is
      // the drag itself — ending here would close the panel on the first pixel
      // outside the pill and drag-to-switch would never reach a second row.
      if (armed.current) return;
      end();
    },
    onContextMenu(event: SyntheticEvent) {
      event.preventDefault();
    }
  });

  /**
   * Wrap a click handler so a completed hold consumes exactly one click — the
   * click of the row it held, named by `value`. Scoping it to the row is what
   * keeps a stray click from any other row from spending the flag on itself.
   */
  const wrapClick = (value: T, run: () => void) => () => {
    if (didHold.current === value) {
      didHold.current = null;
      return;
    }
    run();
  };

  // A pin outlives the press that made it, so it wins; `held` is only ever set
  // while a finger is down, and arming clears the pin to keep the two apart.
  const active = pinned ?? held;

  return { held, pinned, active, start, end, clear, pin, unpin, toggle, handlers, wrapClick };
}
